// components/common/charts/StandardBarChart.jsx - FIXED for negative values
import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import {
  CHART_COLORS,
  formatCompactNumber,
  formatChartCurrency,
  MobileLegend,
  CustomChartDot,
  getResponsiveChartConfig,
  DesktopLegend,
} from "../../utils/chartUtils";

/**
 * StandardBarChart Component - FIXED VERSION
 * Properly handles positive AND negative values with correct colors
 */
export const StandardBarChart = ({
  data,
  config = {},
  title = null,
  showLegend = true,
}) => {
  const [showMobileLegend, setShowMobileLegend] = useState(false);

  // Default config values
  const {
    dataKey = "value",
    comparisonKey = null,
    showComparison = false,
    showGrowth = false,
    showYTDLine = false,
    showZeroLine = true,
    usePositiveNegativeColors = false,
    useProjectedColors = false,
    positiveColor = CHART_COLORS.actual, // Teal (#10B981)
    negativeColor = CHART_COLORS.negative, // Red (#EF4444)
    projectedColor = CHART_COLORS.projected, // Light violet (#E9D5FF)
    color = CHART_COLORS.primary,
    comparisonColor = CHART_COLORS.lastYear,
    valueFormatter = formatChartCurrency,
    comparisonLabel = "Last Year",
    labelKey = "label",
    barName = "Current",
    comparisonName = "Last Year",
  } = config;

  // Process data with proper calculations
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    return data.map(item => ({
      ...item,
      isNegative: item[dataKey] < 0,
      isProjected: item.isFuture || false,
      absValue: Math.abs(item[dataKey]),
    }));
  }, [data, dataKey]);

  // Calculate YTD average
  const ytdStats = useMemo(() => {
    const actualData = chartData.filter(d => !d.isProjected);
    if (actualData.length === 0) return null;
    
    const total = actualData.reduce((sum, d) => sum + d[dataKey], 0);
    const average = total / actualData.length;
    
    return { total, average, count: actualData.length };
  }, [chartData, dataKey]);

  // Get responsive config
  const responsiveConfig = getResponsiveChartConfig();

  // Determine bar color based on value and settings
  const getBarColor = (dataPoint) => {
    // Priority 1: Positive/Negative colors
    if (usePositiveNegativeColors) {
      if (dataPoint.isNegative) {
        // Negative values: use pink/red colors
        return dataPoint.isProjected ? '#fecaca' : negativeColor; // light pink : dark pink/red
      } else {
        // Positive values: use teal/violet colors
        return dataPoint.isProjected ? projectedColor : positiveColor; // violet : teal
      }
    }
    
    // Priority 2: Actual/Projected colors only
    if (useProjectedColors) {
      return dataPoint.isProjected ? projectedColor : positiveColor;
    }
    
    // Default: Use configured color
    return color;
  };

  // Build legend items
  const legendItems = useMemo(() => {
    const items = [];
    
    if (usePositiveNegativeColors) {
      items.push(
        { color: positiveColor, label: "Actual (Positive)", type: "bar" },
        { color: projectedColor, label: "Projected (Positive)", type: "bar" },
        { color: negativeColor, label: "Actual (Negative)", type: "bar" },
        { color: '#fecaca', label: "Projected (Negative)", type: "bar" }
      );
    } else if (useProjectedColors) {
      items.push(
        { color: positiveColor, label: "Actual", type: "bar" },
        { color: projectedColor, label: "Projected", type: "bar" }
      );
    } else {
      items.push({ color, label: barName, type: "bar" });
    }
    
    if (showComparison && comparisonKey) {
      items.push({ 
        color: comparisonColor, 
        label: comparisonName, 
        type: "line" 
      });
    }
    
    return items;
  }, [usePositiveNegativeColors, useProjectedColors, positiveColor, negativeColor, projectedColor, color, barName, showComparison, comparisonKey, comparisonColor, comparisonName]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const mainItem = payload.find(p => p.dataKey === dataKey);
    const comparisonItem = comparisonKey 
      ? payload.find(p => p.dataKey === comparisonKey)
      : null;

    if (!mainItem) return null;

    const currentValue = mainItem.value;
    const isProjected = mainItem.payload.isProjected;

    return (
      <div
        style={{
          padding: "12px",
          background: "white",
          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
          borderRadius: "5px",
          fontSize: "14px",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{label}</div>

        {/* Main value */}
        <div style={{ marginBottom: "8px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <span style={{ color: mainItem.fill, fontWeight: "500" }}>
              {mainItem.name}:
            </span>
            <span style={{ fontWeight: "600" }}>
              {valueFormatter(currentValue)}
            </span>
          </div>
          {isProjected && (
            <div style={{ color: CHART_COLORS.axisText, fontSize: "12px", marginTop: "4px" }}>
              (Projected)
            </div>
          )}
        </div>

        {/* Comparison section */}
        {comparisonItem && comparisonItem.value !== undefined && (
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              marginTop: "8px",
              paddingTop: "6px",
              fontSize: "13px",
              color: CHART_COLORS.axisText,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                marginBottom: "4px",
              }}
            >
              <span>{comparisonLabel}:</span>
              <span style={{ fontWeight: "500" }}>
                {valueFormatter(comparisonItem.value)}
              </span>
            </div>
            {showGrowth && comparisonItem.value !== 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "16px",
                }}
              >
                <span>Growth:</span>
                <span
                  style={{
                    fontWeight: "600",
                    color:
                      currentValue > comparisonItem.value 
                        ? CHART_COLORS.growthPositive 
                        : CHART_COLORS.growthNegative,
                  }}
                >
                  {(
                    ((currentValue - comparisonItem.value) / Math.abs(comparisonItem.value)) *
                    100
                  ).toFixed(1)}
                  %
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-4xl mb-2 text-gray-400">📊</div>
          <div className="text-gray-500 font-medium text-sm sm:text-base">
            No data available
          </div>
        </div>
      </div>
    );
  }

  // Calculate domain for Y-axis to properly show negative values
  const hasNegativeValues = chartData.some(d => d[dataKey] < 0);
  const allValues = chartData.map(d => d[dataKey]);
  const minValue = Math.min(...allValues, 0);
  const maxValue = Math.max(...allValues, 0);

  return (
    <div className="relative w-full">
      {/* Optional Title */}
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}

      {/* Mobile Legend */}
      {showLegend && (
        <MobileLegend 
          legendItems={legendItems}
          showLegend={showMobileLegend} 
          onToggle={() => setShowMobileLegend(!showMobileLegend)} 
        />
      )}

      {/* Chart Container */}
      <div className="w-full h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={responsiveConfig.margin}
            barCategoryGap={responsiveConfig.barCategoryGap}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_COLORS.gridLines}
              vertical={false}
            />

            {/* X-AXIS */}
            <XAxis
              dataKey={labelKey}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.xAxisConfig.fontSize,
                fill: CHART_COLORS.axisText,
              }}
              angle={responsiveConfig.xAxisConfig.angle}
              textAnchor={responsiveConfig.xAxisConfig.textAnchor}
              height={responsiveConfig.xAxisConfig.height}
            />

            {/* Y-AXIS with proper domain for negative values */}
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.yAxisConfig.fontSize,
                fill: CHART_COLORS.axisText,
              }}
              tickFormatter={(value) => formatCompactNumber(Math.round(value))}
              domain={[
                (dataMin) => hasNegativeValues ? Math.floor(dataMin * 1.1) : 0,
                (dataMax) => Math.ceil(dataMax * 1.1)
              ]}
              width={responsiveConfig.yAxisConfig.width}
            />

            {/* TOOLTIP */}
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(4, 32, 43, 0.1)" }}
            />

            {/* DESKTOP LEGEND */}
            {showLegend && (
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{
                  paddingTop: "20px",
                  fontSize: "14px",
                  display: typeof window !== 'undefined' && window.innerWidth < 640 ? 'none' : 'block',
                }}
                content={() => <DesktopLegend legendItems={legendItems} />}
              />
            )}

            {/* ZERO LINE (for negative values) */}
            {showZeroLine && hasNegativeValues && (
              <ReferenceLine 
                y={0} 
                stroke={CHART_COLORS.neutral} 
                strokeWidth={2}
              />
            )}

            {/* YTD AVERAGE LINE */}
            {showYTDLine && ytdStats && (
              <ReferenceLine
                y={ytdStats.average}
                stroke={CHART_COLORS.gridLines}
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: `YTD Avg: ${valueFormatter(ytdStats.average)}`,
                  position: "right",
                  fill: CHART_COLORS.axisText,
                  fontSize: 12,
                }}
              />
            )}

            {/* MAIN BAR with Cell for individual colors */}
            <Bar
              dataKey={dataKey}
              name={barName}
              maxBarSize={50}
              radius={[4, 4, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry)} />
              ))}
            </Bar>

            {/* COMPARISON LINE (Optional) */}
            {showComparison && comparisonKey && (
              <Line
                type="monotone"
                dataKey={comparisonKey}
                stroke={comparisonColor}
                strokeWidth={2}
                name={comparisonName}
                dot={<CustomChartDot dataKey={comparisonKey} />}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default StandardBarChart;